import { TypeChecker } from '@attain-apps/type-guards'

/**
 * The payment period used when generating BCH payment justifications.
 *
 * One approved range applies to every document in a draw, which is why the
 * client's justification formula hard-codes it. It has no default here: the
 * range moves every month, and guessing it would put a wrong date in front of a
 * federal sponsor.
 *
 * Dates are ISO date strings in `YYYY-MM-DD` format.
 */
export interface Period {
  readonly startDate: string
  readonly endDate: string
}
export namespace Period {
  export const isPeriod: TypeChecker<Period> = TypeChecker.Factory.object({
    endDate: TypeChecker.isString,
    startDate: TypeChecker.isString,
  })

  /**
   * Whether an award has expired for this payment period.
   *
   * An award is expired when it ended before the month containing the period's
   * end date. The outline states the rule against the May cycle: for a
   * `4/24/26-5/21/26` period, "any Award End Date before May will need a
   * different date in the formula".
   *
   * Expired awards get no generated justification — the outline requires each
   * one to be reviewed and its language written by hand — so this only decides
   * which rows are held back for that review.
   *
   * An award with no end date is not expired: the date is missing, not past.
   */
  export function isExpired(
    period: Readonly<Period>,
    awardEndDate: string | undefined,
  ): boolean {
    /**
     * ISO dates start with `YYYY-MM`, so keeping those first seven characters
     * and appending `-01` gives the first day of the period's ending month. For
     * an end date of `2026-05-21`, that is `2026-05-01`.
     */
    const periodMonthStart = `${period.endDate.slice(0, 7)}-01`
    return awardEndDate !== undefined && awardEndDate < periodMonthStart
  }

  /** Formats the period the way the justification sentence reads it. */
  export function format(period: Readonly<Period>): string {
    return `${_formatDate(period.startDate)}-${_formatDate(period.endDate)}`
  }

  /** Formats one ISO date as `M/D/YY`. */
  function _formatDate(date: string) {
    const [year = '', month = '', day = ''] = date.split('-')
    return `${Number(month)}/${Number(day)}/${year.slice(-2)}`
  }
}
